import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CreditCard, Check, X, Package, Crown, ArrowRight, Info } from "lucide-react";
import { PageHero } from "../components/FormKit";
import { getMembershipMe, getMySubscription, openBillingPortal } from "../lib/hub";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const money = (v) => (v == null ? "—" : `$${Number(v).toFixed(2)}`);

const PUBLIC_PLAN_IDS = new Set([
  "build_your_store",
  "pro_ecommerce",
]);

const publicPlanName = (access) =>
  !access?.plan_id
    ? "No active membership"
    : PUBLIC_PLAN_IDS.has(access.plan_id)
      ? access.plan_name
      : "Legacy Seller Membership";
const Row = ({ label, ok, value }) => (
  <div className="flex items-center justify-between py-2.5 border-b border-black/5 text-sm">
    <span className="text-muted-foreground">{label}</span>
    {value !== undefined ? <span className="font-bold">{value}</span>
      : ok ? <span className="flex items-center gap-1 text-green-700 font-bold"><Check className="w-4 h-4" /> Yes</span>
      : <span className="flex items-center gap-1 text-black/40"><X className="w-4 h-4" /> No</span>}
  </div>
);

export default function MyMembership() {
  const { user, loading } = useAuth();
  const [data, setData] = useState(null);
  const [sub, setSub] = useState(null);
  const [portalBusy, setPortalBusy] = useState(false);

  useEffect(() => {
    if (user) {
      getMembershipMe().then(setData).catch(() => setData(false));
      getMySubscription().then((d) => setSub(d.subscription)).catch(() => {});
    }
  }, [user]);

  const manageBilling = async () => {
    setPortalBusy(true);
    try {
      const { portal_url } = await openBillingPortal();
      window.location.href = portal_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail?.reason === "no_subscription"
        ? "No active subscription to manage yet."
        : "Could not open the billing portal. Please try again.");
      setPortalBusy(false);
    }
  };

  if (!loading && !user) return (
    <div>
      <PageHero eyebrow="My Membership" title="Sign in to view your membership" subtitle="Log in to see your plan, product usage and access." />
      <div className="px-4 py-12 text-center"><Link to="/login" className="inline-block bg-taven-black text-white px-6 py-3 text-xs font-bold uppercase tracking-wider" data-testid="mm-login-cta">Sign In</Link></div>
    </div>
  );

  const acc = data?.access; const usage = data?.usage;
  const isPro = acc?.included_with_pro_active;

  return (
    <div data-testid="my-membership-page">
      <PageHero eyebrow="My Membership" title="Your TAVEN Membership"
        subtitle="Your current plan, access and product usage." />
      <div className="px-4 md:px-8 lg:px-12 mx-auto max-w-4xl py-10">
        {!data ? (
          <div className="h-64 bg-black/5 animate-pulse" />
        ) : (
          <>
            <div className={`relative border p-6 mb-6 ${isPro ? "border-taven-gold bg-taven-gold/5" : "border-black/10 bg-white"}`} data-testid="mm-current">
              {isPro && <Crown className="absolute top-5 right-5 w-6 h-6 text-taven-gold" />}
              <div className="flex items-center gap-2"><CreditCard className="w-5 h-5 text-taven-gold" />
                <h2 className="text-2xl font-black font-display" data-testid="mm-plan-name">{publicPlanName(acc)}</h2></div>
              <div className="flex flex-wrap items-center gap-3 mt-2">
                <span className={`text-[11px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${acc.status === "active" || acc.status === "complimentary" || acc.status === "trial" ? "bg-green-100 text-green-700" : "bg-black/10 text-black/60"}`} data-testid="mm-status">{acc.status}</span>
                {acc.effective_price != null && <span className="text-sm font-bold">{money(acc.effective_price)}/mo</span>}
                <span className="text-[10px] uppercase tracking-wider text-taven-orange font-bold" data-testid="mm-billing-status">Billing managed by TAVEN</span>
              </div>
              {!acc.plan_id && (
                <Link to="/membership" className="inline-flex items-center gap-1 mt-4 bg-taven-black text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-taven-orange" data-testid="mm-join-cta">Choose a Membership <ArrowRight className="w-3.5 h-3.5" /></Link>
              )}
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="border border-black/10 bg-white p-5">
                <h3 className="font-black font-display uppercase text-sm tracking-wide mb-2">Access</h3>
                <Row label="Seller-store access" ok={acc.seller_tools || isPro} />
                <Row label="External marketplace eligible" ok={acc.external_marketplace_eligible} />
              </div>
              <div className="border border-black/10 bg-white p-5">
                <h3 className="font-black font-display uppercase text-sm tracking-wide mb-2 flex items-center gap-1"><Package className="w-4 h-4" /> Product Usage</h3>
                {usage?.unlimited ? (
                  <p className="py-6 text-center"><span className="text-3xl font-black font-display text-taven-gold">Unlimited</span><br /><span className="text-xs text-muted-foreground">{usage.used} products in use</span></p>
                ) : usage?.limit != null ? (
                  <>
                    <div className="flex items-end justify-between mb-2"><span className="text-3xl font-black font-display" data-testid="mm-usage">{usage.used}</span><span className="text-sm text-muted-foreground">/ {usage.limit}</span></div>
                    <div className="h-2 bg-black/10 rounded-full overflow-hidden"><div className="h-full bg-taven-gold" style={{ width: `${Math.min(100, (usage.used / (usage.limit || 1)) * 100)}%` }} /></div>
                    <p className="text-xs text-muted-foreground mt-2">{usage.remaining} remaining</p>
                  </>
                ) : <p className="py-6 text-center text-sm text-muted-foreground">No active seller plan.</p>}
              </div>
            </div>

            {isPro && (
              <div className="mt-6 border border-taven-gold/40 bg-taven-gold/10 p-5" data-testid="mm-pro-included">
                <h3 className="flex items-center gap-2 font-black font-display text-sm uppercase tracking-wide"><Info className="w-4 h-4 text-taven-gold" /> Included with Pro E-Commerce Membership</h3>
                <ul className="grid sm:grid-cols-2 gap-1.5 mt-3 text-sm">
                  {["Unlimited seller products", "Seller store access", "Seller Product Catalog", "Advanced storefront tools", "Sell on Other Marketplaces eligibility", "Creator and affiliate business tools"].map((f) => (
                    <li key={f} className="flex items-start gap-2"><Check className="w-4 h-4 text-taven-gold shrink-0 mt-0.5" />{f}</li>
                  ))}
                </ul>
                <p className="text-xs text-taven-orange font-bold mt-3">
                  These seller capabilities are included in the Pro plan.
                </p>
              </div>
            )}

            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              {sub?.stripe_subscription_id && (
                <button onClick={manageBilling} disabled={portalBusy} className="inline-flex items-center gap-1 bg-taven-black text-white px-5 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-taven-orange disabled:opacity-60" data-testid="mm-manage-billing">
                  {portalBusy ? "Opening…" : "Manage Billing"} <CreditCard className="w-3.5 h-3.5" />
                </button>
              )}
              <Link to="/membership" className="inline-flex items-center gap-1 border border-black/15 px-5 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-taven-gray" data-testid="mm-upgrade">View Upgrade Options <ArrowRight className="w-3.5 h-3.5" /></Link>
            </div>
            {sub?.cancel_at_period_end && (
              <p className="mt-3 text-center text-xs text-taven-orange font-bold" data-testid="mm-cancel-notice">Your membership is set to cancel at the end of the current paid period{sub?.current_period_end ? ` (${new Date(sub.current_period_end).toLocaleDateString()})` : ""}. You keep full access until then.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
